import React, { useState } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { SalesForm } from '../../components/SalesForm';
import { HeartPulse, ClipboardList, Calendar, Shield, Users, CheckCircle, ArrowRight, Bot } from 'lucide-react';
export function HealthcareSolutionPage() {
  const [isSalesFormOpen, setIsSalesFormOpen] = useState(false);
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1160">
      <Header data-id="element-1161" />
      <main className="flex-grow" data-id="element-1162">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24" data-id="element-1163">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1164">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-1165">
              <div data-id="element-1166">
                <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1167">
                  AI Solutions for Healthcare
                </h1>
                <p className="text-xl text-gray-300 mb-8" data-id="element-1168">
                  Transform patient care and streamline administrative workflows
                  with intelligent AI agents that enhance efficiency, improve
                  outcomes, and reduce staff burnout.
                </p>
                <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1169">
                  Request a Demo
                </button>
              </div>
              <div className="relative" data-id="element-1170">
                <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Healthcare professional" className="rounded-lg shadow-lg" data-id="element-1171" />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg" data-id="element-1172">
                  <HeartPulse className="text-black" size={32} data-id="element-1173" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Key Benefits */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1174">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1175">
            <div className="text-center mb-16" data-id="element-1176">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1177">
                Key Benefits for Healthcare Organizations
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1178">
                Our AI agents address the unique challenges faced by hospitals,
                clinics, and healthcare providers.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1179">
              <BenefitCard icon={<Calendar size={24} data-id="element-1181" />} title="Appointment Management" description="Automate scheduling, reminders, and follow-ups to reduce no-shows and optimize provider utilization." data-id="element-1180" />
              <BenefitCard icon={<ClipboardList size={24} data-id="element-1183" />} title="Documentation Assistance" description="Streamline clinical documentation with AI-powered note-taking and coding suggestions." data-id="element-1182" />
              <BenefitCard icon={<Shield size={24} data-id="element-1185" />} title="Compliance Management" description="Stay on top of regulatory requirements with automated compliance checks and documentation." data-id="element-1184" />
              <BenefitCard icon={<Users size={24} data-id="element-1187" />} title="Patient Communication" description="Enhance patient engagement with automated reminders, follow-ups, and responses to common inquiries." data-id="element-1186" />
              <BenefitCard icon={<CheckCircle size={24} data-id="element-1189" />} title="Care Coordination" description="Improve care transitions and team communication with AI-powered coordination tools." data-id="element-1188" />
              <BenefitCard icon={<Bot size={24} data-id="element-1191" />} title="Predictive Analytics" description="Identify at-risk patients and optimize resource allocation with predictive insights." data-id="element-1190" />
            </div>
          </div>
        </div>
        {/* Features */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1192">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1193">
            <div className="text-center mb-16" data-id="element-1194">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1195">
                Key Features for Healthcare Organizations
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1196">
                Our AI agents offer specialized capabilities designed for the
                unique needs of healthcare providers.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1197">
              <FeatureCard title="Intelligent Appointment Scheduling" description="AI agents that manage complex scheduling requirements, patient preferences, and provider availability to optimize clinic flow." data-id="element-1198" />
              <FeatureCard title="Automated Patient Outreach" description="Proactive communication for appointment reminders, follow-ups, and health maintenance notifications." data-id="element-1199" />
              <FeatureCard title="Insurance Verification" description="Streamline the verification process with automated eligibility checks and benefit explanations." data-id="element-1200" />
              <FeatureCard title="Clinical Documentation Assistance" description="AI-powered tools to help providers create comprehensive, compliant clinical notes more efficiently." data-id="element-1201" />
              <FeatureCard title="Referral Management" description="Automate the referral process from initial request to scheduling and follow-up." data-id="element-1202" />
              <FeatureCard title="Patient Portal Integration" description="Enhance your patient portal with AI assistants that provide 24/7 support for common questions and requests." data-id="element-1203" />
            </div>
          </div>
        </div>
        {/* How It Works */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-1204">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1205">
            <div className="text-center mb-16" data-id="element-1206">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1207">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1208">
                Implementing Agentter.ai in your healthcare organization is
                straightforward and designed to meet strict privacy and security
                requirements.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1209">
              <StepCard number="1" title="HIPAA-Compliant Integration" description="Connect with your existing EHR, practice management, and communication systems with our secure, HIPAA-compliant integrations." data-id="element-1210" />
              <StepCard number="2" title="Configure Your Agents" description="Define the tasks you want to automate using natural language, with built-in compliance guardrails." data-id="element-1211" />
              <StepCard number="3" title="Deploy and Monitor" description="Launch your AI agents with comprehensive audit trails and continuous performance monitoring." data-id="element-1212" />
            </div>
          </div>
        </div>
        {/* Security & Compliance */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1213">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1214">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-1215">
              <div data-id="element-1216">
                <h2 className="text-3xl font-bold mb-6" data-id="element-1217">
                  Security & Compliance
                </h2>
                <p className="text-lg text-gray-600 mb-6" data-id="element-1218">
                  We understand that security and compliance are paramount in
                  healthcare. Agentter.ai is built from the ground up with HIPAA
                  compliance and patient data protection as core principles.
                </p>
                <div className="space-y-4 mb-6" data-id="element-1219">
                  <SecurityItem text="HIPAA-compliant infrastructure and processes" data-id="element-1220" />
                  <SecurityItem text="End-to-end encryption for all patient data" data-id="element-1221" />
                  <SecurityItem text="Comprehensive audit trails for all AI agent actions" data-id="element-1222" />
                  <SecurityItem text="Role-based access controls and multi-factor authentication" data-id="element-1223" />
                  <SecurityItem text="Regular security assessments and penetration testing" data-id="element-1224" />
                  <SecurityItem text="Business Associate Agreements (BAA) provided" data-id="element-1225" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1226">
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center" data-id="element-1227">
                  <span className="font-bold text-xl" data-id="element-1228">HIPAA</span>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center" data-id="element-1229">
                  <span className="font-bold text-xl" data-id="element-1230">SOC 2</span>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center" data-id="element-1231">
                  <span className="font-bold text-xl" data-id="element-1232">HITRUST</span>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center" data-id="element-1233">
                  <span className="font-bold text-xl" data-id="element-1234">ISO 27001</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-black text-white py-16" data-id="element-1235">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1236">
            <h2 className="text-3xl font-bold mb-6" data-id="element-1237">
              Ready to transform your healthcare operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-1238">
              Schedule a personalized demo to see how Agentter.ai can automate
              and optimize your healthcare workflows while maintaining the
              highest security standards.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-1239">
              <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1240">
                Request a Demo
              </button>
              <a href="#" className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors" data-id="element-1241">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1242" />
      {/* Sales Form Modal */}
      <SalesForm isOpen={isSalesFormOpen} onClose={() => setIsSalesFormOpen(false)} agentType="Healthcare Solutions" data-id="element-1243" />
    </div>;
}
function BenefitCard({
  icon,
  title,
  description
}) {
  return <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1244">
      <div className="bg-black text-white p-3 rounded-lg inline-block mb-4" data-id="element-1245">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1246">{title}</h3>
      <p className="text-gray-600" data-id="element-1247">{description}</p>
    </div>;
}
function StepCard({
  number,
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1248">
      <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold" data-id="element-1249">
        {number}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1250">{title}</h3>
      <p className="text-gray-600" data-id="element-1251">{description}</p>
    </div>;
}
function ResultItem({
  text
}) {
  return <div className="flex items-start" data-id="element-1252">
      <CheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-1253" />
      <span data-id="element-1254">{text}</span>
    </div>;
}
function SecurityItem({
  text
}) {
  return <div className="flex items-start" data-id="element-1255">
      <Shield className="text-black mr-2 mt-1 flex-shrink-0" size={20} data-id="element-1256" />
      <span data-id="element-1257">{text}</span>
    </div>;
}
function FeatureCard({
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1258">
      <h3 className="text-xl font-semibold mb-3" data-id="element-1259">{title}</h3>
      <p className="text-gray-600" data-id="element-1260">{description}</p>
    </div>;
}